import { useSession } from "@/lib/auth/auth";
import { useMutation, useQuery } from "@apollo/client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useParams } from "@tanstack/react-router";
import Papa from "papaparse";
import type React from "react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";

import { companySetupApi } from "@/apis/custom/companySetup";
import {
  COMPANY_QUERY,
  UPDATE_COMPANY_MUTATION,
} from "@/apis/gql/apolloClient";
import { DELETE_COMPANY_MUTATION } from "@/apis/gql/apolloClient";
import type { ParsedAccountRecord } from "@/apis/gql/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { AccountFileUpload } from "@/features/company-setup/create/data-import-step/account-file-upload";
import { useTenant } from "@/providers/session";
import { useGetAllCompanies } from "@/queries/companies/get-all";
import { Check, ExternalLink, Loader2 } from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/$company/buchhaltung/mandant-bearbeiten",
)({
  component: AdminCompanySetup,
});

function AdminCompanySetup() {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();
  const { company: companyId } = useParams({
    from: "/_authenticated/$company",
  });

  const { tenantId, companyName } = useTenant();

  const [suppliersCsv, setSuppliersCsv] = useState<File | null>(null);
  const [accountsCsv, setAccountsCsv] = useState<File | null>(null);
  const [costCentersCsv, setCostCentersCsv] = useState<File | null>(null);
  const [datevAccountsFile, setDatevAccountsFile] = useState<File | null>(null);
  const [parsedAccounts, setParsedAccounts] = useState<
    ParsedAccountRecord[] | null
  >(null);
  const [datevUploadSuccess, setDatevUploadSuccess] = useState(false);
  const [datevUploadLoading, setDatevUploadLoading] = useState(false);

  const [userAccessEmails, setUserAccessEmails] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [useEmailFilter, setUseEmailFilter] = useState(false);

  const [isActive, setIsActive] = useState(true);

  const { data } = useQuery(COMPANY_QUERY, {
    variables: { companyId: tenantId },
  });

  const form = useForm({
    defaultValues: {
      skr: "03",
      accountLength: String(data?.company?.accountLength) || "4",
    },
  });

  useEffect(() => {
    if (data?.company?.accountLength) {
      form.setValue("accountLength", data?.company?.accountLength.toString());
    }
  }, [data?.company?.accountLength, form]);

  const [updateCompanyStatus] = useMutation(UPDATE_COMPANY_MUTATION);

  useEffect(() => {
    const fetchCompanyStatus = async () => {
      try {
        console.log("COMPANY DATA", data);
        setIsActive(data?.company?.status === "active");
      } catch (error) {
        toast.error("Failed to fetch company status");
      }
    };

    if (tenantId) {
      fetchCompanyStatus();
    }
  }, [tenantId, data]);
  const { refetch } = useGetAllCompanies();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [deleteCompanyMutation] = useMutation(DELETE_COMPANY_MUTATION);

  if (isPending || !tenantId) {
    return <div>Loading...</div>;
  }

  if (!session || session?.user?.systemRole !== "superadmin") {
    navigate({ to: "/" });
    return null;
  }

  const handleGiveAccess = async () => {
    try {
      const payload = useEmailFilter
        ? { emailFilter: emailFilter, isFilter: true }
        : {
            emails: userAccessEmails.split(",").map((email) => email.trim()),
            isFilter: false,
          };

      await companySetupApi.giveAccess({
        ...payload,
      });
      toast.success("Access granted successfully");
    } catch (error) {
      toast.error("Failed to grant access");
    }
  };

  const handleUploadSuppliers = async () => {
    if (!suppliersCsv) return;

    const fileContent = await suppliersCsv.text();

    // Split the content into lines
    const lines = fileContent.split("\n");

    // Remove the first line (junk data)
    lines.shift();

    const csvWithoutFirstRow = lines.join("\n");

    const parseResult = await new Promise<
      Papa.ParseResult<Record<string, string>>
    >((resolve, reject) => {
      Papa.parse(csvWithoutFirstRow, {
        header: true,
        complete: resolve,
        error: reject,
      });
    });

    const columnsToKeep = [
      "Name (Adressattyp Unternehmen)",
      "EU-Land",
      "EU-UStID",
      "Konto",
    ];

    const filteredData = parseResult.data.map((row) =>
      Object.fromEntries(
        Object.entries(row).filter(([key]) => columnsToKeep.includes(key)),
      ),
    );

    const filteredCsv = Papa.unparse(filteredData);

    const filteredFile = new File([filteredCsv], suppliersCsv.name, {
      type: "text/csv",
    });

    const formData = new FormData();
    formData.append("file", filteredFile);
    try {
      await companySetupApi.uploadSuppliers(formData);
      toast.success("Suppliers uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload suppliers");
    }
  };

  const handleUploadAccounts = async () => {
    if (!accountsCsv) return;
    const formData = new FormData();
    formData.append("file", accountsCsv);
    try {
      await companySetupApi.uploadAccounts(formData);
      toast.success("Accounts uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload accounts");
    }
  };

  const handleUploadCostCenters = async () => {
    if (!costCentersCsv) return;
    const formData = new FormData();
    formData.append("file", costCentersCsv);
    try {
      await companySetupApi.uploadCostCenters(formData);
      toast.success("Cost centers uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload cost centers");
    }
  };

  const handleUploadDatevAccounts = async () => {
    if (!parsedAccounts || !datevAccountsFile) return;

    setDatevUploadLoading(true);
    try {
      const accountLength = form.watch("accountLength");
      await companySetupApi.createAccounts({
        accounts: parsedAccounts,
        accountLength: Number(accountLength) as 4 | 5 | 6,
      });
      toast.success("Datev accounts created successfully");
      setDatevUploadSuccess(true);
    } catch (error) {
      toast.error(
        (error as any)?.response?.data?.error ||
          "Failed to create Datev accounts",
      );
    } finally {
      setDatevUploadLoading(false);
    }
  };

  const handleStatusToggle = async (checked: boolean) => {
    try {
      await updateCompanyStatus({
        variables: {
          companyId: tenantId,
          updatedFields: {
            status: checked ? "active" : "pending",
          },
        },
      });
      setIsActive(checked);
      toast.success("Company status updated successfully");
    } catch (error) {
      toast.error("Failed to update company status");
      setIsActive(!checked);
    }
  };

  const handleDeleteCompany = async () => {
    try {
      if (!tenantId) {
        toast.error("Company ID not found");
        return;
      }
      await deleteCompanyMutation({
        variables: {
          companyId: tenantId,
        },
      });
      await refetch();
      toast.success("Company deleted successfully");
      navigate({ to: "/" });
    } catch (error) {
      toast.error("Failed to delete company");
      console.error("Error deleting company:", error);
    }
    setIsDeleteDialogOpen(false);
  };

  return (
    <>
      <div className="p-4 h-full flex flex-col gap-4 overflow-auto">
        <div className="grid w-full gap-2">
          <h1 className="text-3xl font-semibold text-primary">
            {`Mandant Bearbeiten: ${companyName}`}
          </h1>
          {tenantId && (
            <div className="flex gap-4 mb-4">
              <Button variant="outline" asChild>
                <a
                  href={`https://tabular.metabaseapp.com/question/120-company-infos-by-id?CompanyId=${encodeURIComponent(
                    tenantId,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Metabase Company Info
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a
                  href={`https://tabular.metabaseapp.com/question/119-user-accesses-by-id?CompanyId=${encodeURIComponent(
                    tenantId,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Metabase User Accesses
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a
                  href={`https://tabular-admin.fly.dev/admin/companies/${tenantId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Admin Tool
                </a>
              </Button>
            </div>
          )}
          <div className="flex items-center space-x-4 mt-2 mb-6">
            <Switch
              checked={isActive}
              onCheckedChange={handleStatusToggle}
              id="company-status"
            />
            <Label htmlFor="company-status">
              {isActive ? "Active" : "Pending"}
            </Label>
          </div>
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-xl font-semibold mb-2">Upload Suppliers CSV</h2>
            <p className="text-sm text-gray-600 mb-4">
              Must conform to standard DATEV Suppliers CSV Format: Column
              headers must be on second row, must contain columns Name
              (Adressattyp Unternehmen), EU-UStID, Konto. Can contain other
              columns, these will be discarded.
            </p>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setSuppliersCsv(e.target.files?.[0] || null)}
            />
            <Button
              onClick={handleUploadSuppliers}
              className="mt-2"
              disabled={!tenantId}
            >
              Upload Suppliers
            </Button>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">Upload Accounts CSV</h2>
            <p className="text-sm text-gray-600 mb-4">
              Must conform to standard DATEV Accounts CSV Format: Column headers
              must be on second row, must contain columns Konto von,
              Beschriftung, Definition. All other columns will be discarded.
            </p>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setAccountsCsv(e.target.files?.[0] || null)}
            />
            <Button
              onClick={handleUploadAccounts}
              className="mt-2"
              disabled={!tenantId}
            >
              Upload Accounts
            </Button>
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-2">
              Upload Cost Centers CSV
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Must conform to standard DATEV Cost Centers CSV Format: Column
              headers must be first row, must contain columns:
              Kostenstelle/-träger, Kurzbezeichnung, Langbezeichnung
            </p>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setCostCentersCsv(e.target.files?.[0] || null)}
            />
            <Button
              onClick={handleUploadCostCenters}
              className="mt-2"
              disabled={!tenantId}
            >
              Upload Cost Centers
            </Button>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">
              Import Datev Accounts Setup
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Upload a DATEV accounts file to automatically configure the
              account structure. The system will detect account length and
              validate the file format.
            </p>
            <div className="space-y-4">
              <div>
                <RadioGroup
                  value={form.watch("skr")}
                  onValueChange={(value) =>
                    form.setValue("skr", value as "03" | "04")
                  }
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="03" id="skr-03" />
                    <Label htmlFor="skr-03">SKR03</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="04" id="skr-04" />
                    <Label htmlFor="skr-04">SKR04</Label>
                  </div>
                </RadioGroup>
              </div>
              <FormProvider {...form}>
                <AccountFileUpload
                  label="Datev Accounts File"
                  accept=".csv,.txt,.xlsx,.xls"
                  onChange={(file) => {
                    setDatevAccountsFile(file);
                    if (!file) {
                      setParsedAccounts(null);
                    }
                    setDatevUploadSuccess(false);
                    setDatevUploadLoading(false);
                  }}
                  value={datevAccountsFile}
                  onAccountsParsed={(accounts, accountLength) => {
                    setParsedAccounts(accounts);
                    form.setValue("accountLength", accountLength.toString());
                  }}
                />
              </FormProvider>
            </div>
            <Button
              onClick={handleUploadDatevAccounts}
              className="mt-2"
              disabled={
                !tenantId ||
                !datevAccountsFile ||
                !parsedAccounts ||
                datevUploadLoading
              }
            >
              {datevUploadLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : datevUploadSuccess ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-600" />
                  Upload Datev Accounts
                </>
              ) : (
                "Upload Datev Accounts"
              )}
            </Button>
            {companyId && (
              <div className="mt-2">
                <a
                  href={`/${companyId}/buchhaltung/einstellungen/konten`}
                  className="text-blue-600 hover:underline text-sm"
                >
                  View and manage accounts →
                </a>
              </div>
            )}
          </div>
        </div>
        <div className="space-y-4">
          <h2 className="text-xl font-semibold mb-2">Give Users Access</h2>
          <p className="text-sm text-gray-600 mb-4">
            You can either pass a comma-separated list of user emails, or you
            can set an email filter, which means that we will give access to all
            users whose email contains this substring.
          </p>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useEmailFilter"
                checked={useEmailFilter}
                onCheckedChange={(checked) =>
                  setUseEmailFilter(checked as boolean)
                }
              />
              <Label htmlFor="useEmailFilter">Use Email Filter</Label>
            </div>
            {useEmailFilter ? (
              <div>
                <Label htmlFor="emailFilter">Email Filter</Label>
                <Input
                  id="emailFilter"
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  placeholder="e.g. @company.com"
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="userAccessEmails">
                  User Emails (comma-separated)
                </Label>
                <Input
                  id="userAccessEmails"
                  value={userAccessEmails}
                  onChange={(e) => setUserAccessEmails(e.target.value)}
                  placeholder="user1@example.com, user2@example.com"
                />
              </div>
            )}
            <Button onClick={handleGiveAccess} disabled={!tenantId}>
              Give Access
            </Button>
          </div>
        </div>
        <div className="border-t pt-8">
          <Button
            variant="destructive"
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            Delete Company
          </Button>
        </div>
      </div>
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will soft-delete the company
              {companyName && ` "${companyName}"`}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCompany}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Company
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
